import { useEffect, useState, useCallback } from "react";
import api, { eur } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Ruler, ExternalLink, RefreshCw, Calculator, Check, AlertTriangle, Download, Archive } from "lucide-react";
import { toast } from "sonner";

const cfgMatch = (c, q) => {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [c.installer?.ragione_sociale, c.agent?.name, c.summary?.comune, c.summary?.cliente].some((v) => (v || "").toLowerCase().includes(s));
};
const AdminCfgLine = ({ c }) => (
  <span className="block text-[11px] text-primary" data-testid={`admin-cfg-owner-${c.id}`}>
    {c.installer?.ragione_sociale || "—"} · {c.installer_type || (c.is_partner ? "partner" : "cliente")}{c.agent?.name ? ` · agente ${c.agent.name}` : ""} · {new Date(c.created_at).toLocaleDateString("it-IT")}
  </span>
);
const eurN = (v) => (v == null ? "—" : eur(v));

const panelDims = (m) => {
  if (!m) return { w: null, l: null, wp: null };
  const w = m.larghezza_m ? Math.round(Number(m.larghezza_m) * 1000) : null;
  const l = m.altezza_m ? Math.round(Number(m.altezza_m) * 1000) : null;
  let wp = null;
  for (const [k, v] of Object.entries(m.specifiche || {})) {
    if (k.toLowerCase().includes("potenza") && k.toLowerCase().includes("pannello")) {
      const n = parseFloat(String(v).replace(",", "."));
      if (!Number.isNaN(n)) wp = Math.round(n);
    }
  }
  return { w, l, wp };
};
const dimLabel = (m) => {
  const { w, l, wp } = panelDims(m);
  const parts = [];
  if (w && l) parts.push(`${w}×${l} mm`);
  if (wp) parts.push(`${wp} Wp`);
  return parts.join(" · ");
};

const TIPI = [
  { key: "coppo_tegola", label: "Coppo / Tegola" },
  { key: "lamiera", label: "Lamiera grecata" },
  { key: "piano", label: "Tetto piano" },
];
const STAFFE = [
  { code: "STH1-5-AR", label: "Staffa tegole regolabile (STH1-5-AR)" },
  { code: "STH1-7-AR", label: "Staffa tegole vert./oriz. (STH1-7-AR)" },
  { code: "M10X250-AR", label: "Vite tirafondo M10x250 (3ª alternativa)" },
];
const TILT_KEYS = ["inclinazione_gradi", "inclinazione", "tilt", "tilt_deg", "gradi", "angolo", "angolo_gradi", "pitch"];
const roofTilt = (s) => {
  if (!s) return null;
  for (const k of TILT_KEYS) {
    const v = s[k];
    if (v != null && !Number.isNaN(parseFloat(v))) return parseFloat(v);
  }
  return null;
};
const nearestGradi = (t) => (t == null ? "25" : (Math.abs(t - 10) <= Math.abs(t - 25) ? "10" : "25"));
const ZAVORRE = [
  { code: "none", label: "Nessuna zavorra" },
  { code: "L1000", label: "Massello calcestruzzo 1000mm (L1000)" },
  { code: "L1200", label: "Massello calcestruzzo 1200mm (L1200)" },
];

// riconosce l'orientamento orizzontale: it (orizz/o), en (horizontal/h) e landscape (moduli orizzontali)
const isHoriz = (o) => {
  const s = (o || "").toLowerCase();
  return s.startsWith("oriz") || s.startsWith("h") || s.startsWith("land");
};

const orientLabel = (o) => {
  const s = (o || "").toLowerCase();
  if (isHoriz(s)) return "moduli orizzontali";
  if (s.startsWith("vert") || s.startsWith("port")) return "moduli verticali";
  return o || "orientamento n/d";
};
const faldeSummary = (falde) => (falde || []).map((f, i) => {
  const rows = (f.rows || []).map((n) => Number(n) || 0);
  const nFile = rows.length;
  const tot = rows.reduce((a, b) => a + b, 0);
  const detail = nFile ? ` · pann./fila: ${rows.join("·")}` : "";
  return `Falda ${i + 1} · ${nFile} file · ${tot} pann. · ${orientLabel(f.orientamento || f.orientation)}${detail}`;
}).join("   |   ");

const WP_TOL = 3;const impliedWp = (summary) => {
  const kwp = Number(summary?.potenza_kwp);
  const n = Number(summary?.n_pannelli);
  if (!kwp || !n) return null;
  return Math.round((kwp * 1000) / n);
};

const DIM_TOL = 8; // mm — tolleranza confronto dimensioni pannello
// Legge il pannello dalla consulenza SE il configuratore lo rimanda (summary.panel),
// altrimenti ricade sulla sola potenza derivata (kWp ÷ n° pannelli).
const configPanel = (summary) => {
  const p = summary?.panel || null;
  const w = p && p.larghezza_mm ? Math.round(Number(p.larghezza_mm)) : null;
  const l = p && p.lunghezza_mm ? Math.round(Number(p.lunghezza_mm)) : null;
  const wp = (p && p.potenza_wp) ? Math.round(Number(p.potenza_wp)) : impliedWp(summary);
  return { model: p?.model || null, w, l, wp };
};
const configPanelExact = (cp) => (cp.w && cp.l ? `${cp.model ? cp.model + " · " : ""}${cp.w}×${cp.l} mm${cp.wp ? ` · ${cp.wp} Wp` : ""}` : "");

const transposeRows = (rows) => {
  const heights = (rows || []).map((n) => Number(n) || 0).filter((h) => h > 0);
  if (!heights.length) return (rows || []).map((n) => Number(n) || 0);
  const m = Math.max(...heights);
  return Array.from({ length: m }, (_, r) => heights.filter((h) => h > r).length);
};

// normalizza la 'matrice' reale (0/1 per cella) dal configuratore, allineata a griglia comune
const normFaldaMatrix = (f) => {
  const m = f?.matrice ?? f?.matrix;
  if (!Array.isArray(m) || !m.length || !m.every((r) => Array.isArray(r))) return null;
  const empty = ["", "0", "false", "none", "null"];
  const mat = m.map((r) => r.map((c) => (empty.includes(String(c).trim().toLowerCase()) ? 0 : 1)));
  const width = Math.max(1, ...mat.map((r) => r.length));
  mat.forEach((r) => { while (r.length < width) r.push(0); });
  // la matrice del configuratore va letta SPECCHIATA destra/sinistra → mirror orizzontale
  return { mat: mat.map((r) => [...r].reverse()), width };
};

function LayoutPreview({ falde, latiFalde }) {
  const list = (falde || []).filter((f) => (f.rows || []).length);
  if (!list.length) return null;
  return (
    <div className="mt-2 rounded-lg border border-input bg-background/50 p-3" data-testid="fissaggi-layout-preview">
      <div className="mb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-primary">Anteprima layout</div>
      <div className="flex flex-wrap items-end gap-4">
        {list.map((f, i) => {
          const horiz = isHoriz(f.orientamento || f.orientation);
          const cw = horiz ? 15 : 9;
          const ch = horiz ? 9 : 15;
          const gap = 2;
          const nm = normFaldaMatrix(f);
          if (nm) {
            // layout REALE dal configuratore: celle allineate al bordo comune, vuoti conservati
            const { mat, width } = nm;
            const w = width * (cw + gap) + gap;
            const h = mat.length * (ch + gap) + gap;
            const tot = mat.reduce((a, r) => a + r.reduce((x, y) => x + y, 0), 0);
            return (
              <figure key={i} className="min-w-0" data-testid={`fissaggi-layout-falda-${i}`}>
                <svg viewBox={`0 0 ${w} ${h}`} width={Math.min(w, 220)} height={(Math.min(w, 220) / w) * h}
                     className="rounded border border-border/60 bg-muted/30" role="img" aria-label={`Falda ${i + 1}`}>
                  {mat.flatMap((row, r) => row.map((v, c) => (v ? (
                    <rect key={`${r}-${c}`} x={gap + c * (cw + gap)} y={gap + r * (ch + gap)}
                          width={cw} height={ch} rx="1" fill="#2f6fed" stroke="#173a86" strokeWidth="0.5" />
                  ) : null)))}
                </svg>
                <figcaption className="mt-1 text-[10px] text-muted-foreground">Falda {i + 1} · {mat.length} file · {tot} pann. · {horiz ? "orizz." : "vert."} · layout reale</figcaption>
              </figure>
            );
          }
          const rows = (f.rows || []).map((n) => Number(n) || 0);
          const cols = Math.max(...rows, 1);
          const origIdx = (falde || []).indexOf(f);
          const lato = (latiFalde && latiFalde[origIdx]) || "corto";
          const modoA = (lato === "corto" && horiz) || (lato === "lungo" && !horiz);
          const effRows = modoA ? rows : transposeRows(rows);
          const w = cols * (cw + gap) + gap;
          const h = rows.length * (ch + gap) + gap;
          const tot = rows.reduce((a, b) => a + b, 0);
          return (
            <figure key={i} className="min-w-0" data-testid={`fissaggi-layout-falda-${i}`}>
              <svg viewBox={`0 0 ${w} ${h}`} width={Math.min(w, 220)} height={(Math.min(w, 220) / w) * h}
                   className="rounded border border-border/60 bg-muted/30" role="img" aria-label={`Falda ${i + 1}`}>
                {rows.map((count, r) => {
                  const rowW = count * (cw + gap) + gap;
                  const offset = (w - rowW) / 2;
                  return Array.from({ length: count }).map((_, cIdx) => (
                    <rect key={`${r}-${cIdx}`} x={offset + gap + cIdx * (cw + gap)} y={gap + r * (ch + gap)}
                          width={cw} height={ch} rx="1" fill="#2f6fed" stroke="#173a86" strokeWidth="0.5" />
                  ));
                })}
              </svg>
              <figcaption className="mt-1 text-[10px] text-muted-foreground">Falda {i + 1} · {effRows.length} file · {tot} pann. · {horiz ? "orizz." : "vert."}{!modoA ? " · Modo B (colonne)" : ""}</figcaption>
            </figure>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-muted-foreground">Schema indicativo della disposizione pannelli per falda. Per la vista satellitare reale usa «Apri consulenza selezionata».</p>
    </div>
  );
}


export default function FissaggiTettoDialog({ open, onOpenChange, installerId, moduleRow, tettoConfig, onApply, forceTipo, inclinatoOnly, pianoOnly, pdfMode, installerName, installerIsPartner, preload, preloadKey, adminAll, link }) {
  const [configs, setConfigs] = useState([]);
  const [loadingCfg, setLoadingCfg] = useState(false);
  const [selected, setSelected] = useState(tettoConfig || null);
  const [cfgQ, setCfgQ] = useState("");
  const instId = adminAll ? selected?.installer?.id : installerId;
  const instName = adminAll ? selected?.installer?.ragione_sociale : installerName;
  const instPartner = adminAll ? !!selected?.is_partner : installerIsPartner;
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [tipo, setTipo] = useState("coppo_tegola");
  const [staffa, setStaffa] = useState("STH1-5-AR");
  const [gradi, setGradi] = useState("25");
  const [zavorra, setZavorra] = useState("L1000");
  const [passoStaffa, setPassoStaffa] = useState("1");
  const [latiFalde, setLatiFalde] = useState([]);
  const [binariOverride, setBinariOverride] = useState(null);
  const [mw, setMw] = useState("");
  const [mh, setMh] = useState("");
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [savingCt, setSavingCt] = useState(false);
  const [staffeInfo, setStaffeInfo] = useState({});

  const loadConfigs = useCallback(() => {
    if (link) { setConfigs([link.config]); return; }
    if (!installerId && !adminAll) return;
    setLoadingCfg(true);
    api.get(adminAll ? "/rete/configs" : `/rete/configs?installer_id=${installerId}`)
      .then((r) => setConfigs((r.data?.configs || []).filter((c) => {
        const f = c.summary?.disposizione_falde || c.disposizione_falde || [];
        const hasLayout = Array.isArray(f) && f.some((x) => (x.rows || []).length > 0);
        if (!hasLayout) return false;
        const rt = c.summary?.roof_type || c.roof_type;
        if (inclinatoOnly) return (c.tool === "tetto" || c.tool === "consulenza") && rt === "inclinato";
        if (pianoOnly) return c.tool === "tetto" && rt === "piano";
        return c.tool === "tetto";
      })))
      .catch(() => setConfigs([]))
      .finally(() => setLoadingCfg(false));
  }, [installerId, inclinatoOnly, pianoOnly, adminAll, link]);

  useEffect(() => { if (open) { loadConfigs(); setResult(null); setSelected(link?.config || tettoConfig || null); } }, [open, loadConfigs, tettoConfig, link]);
  useEffect(() => {
    if (!open) return;
    api.get(link ? "/strumenti/catalogo" : "/listino").then((r) => {
      const map = {};
      (r.data?.products || []).forEach((p) => {
        const code = (p.codice || "").trim();
        if (code) map[code] = { immagine_url: p.immagine_url, descrizione: p.descrizione };
      });
      setStaffeInfo(map);
    }).catch(() => {});
  }, [open, link]);
  useEffect(() => { if (open && forceTipo) setTipo(forceTipo); }, [open, forceTipo]);
  useEffect(() => {
    if (!open) return;
    api.get(link ? "/strumenti/catalogo?tipologia=Moduli%20Fotovoltaici" : "/listino?tipologia=Moduli%20Fotovoltaici&abilitato=1").then((r) => setModules(r.data?.products || [])).catch(() => setModules([]));
  }, [open, link]);

  const falde = selected?.summary?.disposizione_falde || selected?.disposizione_falde || [];
  const hasFalde = falde.some((f) => (f.rows || []).length > 0);
  const roofType = selected?.summary?.roof_type || selected?.roof_type || null;

  // Preseleziona il tipo copertura in base al roof_type del configuratore
  useEffect(() => {
    if (preload) return;
    if (roofType === "piano") setTipo("piano");
    else if (roofType === "inclinato") setTipo((cur) => (cur === "piano" ? "coppo_tegola" : cur));
    const _sg = selected?.summary?.staffa_gradi;
    if (_sg != null && !Number.isNaN(parseFloat(_sg))) setGradi(nearestGradi(parseFloat(_sg)));
    else { const _tilt = roofTilt(selected?.summary); if (_tilt != null) setGradi(nearestGradi(_tilt)); }
    setLatiFalde((falde || []).map(() => "corto"));
    setBinariOverride(null);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, roofType]);

  const selModule = modules.find((m) => m.id === moduleId) || null;
  const selDims = selModule ? panelDims(selModule) : null;
  const selWp = selDims ? selDims.wp : null;
  const autoBinari = 2;
  const effBinari = binariOverride ?? autoBinari;
  const isIncompatible = (c) => {
    if (!selDims) return false;
    const cp = configPanel(c.summary);
    if (selDims.w && selDims.l && cp.w && cp.l)
      return Math.abs(cp.w - selDims.w) > DIM_TOL || Math.abs(cp.l - selDims.l) > DIM_TOL;
    if (selWp && cp.wp) return Math.abs(cp.wp - selWp) > WP_TOL;
    return false;
  };
  useEffect(() => {
    if (!preload && selected && selWp && isIncompatible(selected)) { setSelected(null); setResult(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, selWp]);

  // RIAPRI CONFIGURAZIONE: ricarica gli input da una consulenza tecnica salvata
  useEffect(() => {
    if (!open || !preload) return;
    const p = preload;
    const syn = { id: `ct-${preloadKey || "x"}`, created_at: null, consulenza_url: p.meta?.consulenza_url || null,
      summary: { disposizione_falde: p.disposizione_falde || [], roof_type: (p.tipo_copertura === "piano" ? "piano" : "inclinato"),
        cliente: p.meta?.cliente || null, comune: p.meta?.comune || null, indirizzo: p.meta?.indirizzo || null,
        potenza_kwp: p.meta?.potenza_kwp ?? null } };
    setSelected(syn);
    setTipo(p.tipo_copertura || "coppo_tegola");
    setGradi(String(p.gradi_inclinazione || "25"));
    setPassoStaffa(String(p.passo_staffa ?? "1"));
    setLatiFalde(Array.isArray(p.lati_falde) && p.lati_falde.length ? p.lati_falde : (p.disposizione_falde || []).map(() => p.lato_fissaggio || "corto"));
    setBinariOverride(p.binari_override ?? null);
    setModuleId(p.module_listino_id || "");
    const a = p.articoli || {};
    if (a.staffa) setStaffa(a.staffa);
    if (a.zavorra) setZavorra(a.zavorra);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preload, preloadKey]);

  const openConfiguratore = () => {
    if (!moduleId) { toast.error("Scegli prima il pannello fotovoltaico"); return; }
    window.open(`/configuratore/${installerId}?tool=tetto&module=${moduleId}`, "_blank", "noopener");
    toast.info("Configuratore aperto in una nuova scheda. Al termine torna qui e premi «Aggiorna consulenze».");
  };

  const compute = async () => {
    if (!selected) { toast.error("Seleziona prima una consulenza tetto"); return; }
    setComputing(true);
    try {
      const articoli = { staffa, zavorra };
      const r = await api.post("/crm-quotes/fissaggi/compute", {
        tipo_copertura: tipo,
        disposizione_falde: falde,
        module_listino_id: moduleId || null,
        passo_staffa: passoStaffa ? Number(String(passoStaffa).replace(",", ".")) : 1,
        lato_fissaggio: latiFalde[0] || "corto",
        lati_falde: latiFalde,
        binari_override: binariOverride,
        gradi_inclinazione: gradi,
        articoli,
      });
      setResult(r.data);
      if (r.data.warnings?.length) r.data.warnings.forEach((w) => toast.warning(w));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel calcolo dei fissaggi");
    } finally { setComputing(false); }
  };

  const buildFissPayload = () => {
    const s = selected?.summary || {};
    const fieldImg = s.overlay_image_url || s.satellite_url || s.screenshot_url || s.immagine_url || s.map_url || s.snapshot_url || null;
    return {
      tipo_copertura: tipo,
      disposizione_falde: falde,
      module_listino_id: moduleId || null,
      passo_staffa: passoStaffa ? Number(String(passoStaffa).replace(",", ".")) : 1,
      lato_fissaggio: latiFalde[0] || "corto",
      lati_falde: latiFalde,
      binari_override: binariOverride,
      gradi_inclinazione: gradi,
      articoli: { staffa, zavorra },
      meta: {
        cliente: s.cliente || null,
        installatore: instName || null,
        is_partner: !!instPartner,
        consulenza_date: selected?.created_at || null,
        consulenza_ref: selected?.id ? String(selected.id).slice(-6) : null,
        consulenza_url: selected?.consulenza_url || null,
        comune: s.comune || null,
        indirizzo: s.indirizzo || null,
        potenza_kwp: s.potenza_kwp ?? null,
        satellite_url: fieldImg,
      },
    };
  };

  const downloadPdf = async () => {
    if (!result?.righe?.length) return;
    setPdfLoading(true);
    try {
      const s = selected?.summary || {};
      const payload = buildFissPayload();
      const r = await api.post("/crm-quotes/fissaggi/pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const _cli = (s.cliente || "").trim().replace(/[\s/]+/g, "_");
      const _ref = selected?.id ? String(selected.id).slice(-6) : "";
      const _name = ["Fissaggi", _cli, _ref].filter(Boolean).join("_") || "Fissaggi_consulenza";
      a.href = url; a.download = `${_name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Allegato tecnico PDF generato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella generazione del PDF"); }
    finally { setPdfLoading(false); }
  };

  const saveConsulenza = async () => {
    if (!result?.righe?.length) { toast.error("Calcola prima il dimensionamento"); return; }
    setSavingCt(true);
    try {
      if (link) {
        await api.post("/strumenti/salva", buildFissPayload());
        toast.success("Consulenza tecnica salvata nel configuratore");
      } else {
        await api.post("/consulenze-tecniche/fissaggi", { ...buildFissPayload(), installer_id: instId || null, config_id: selected?.id || null });
        toast.success("Consulenza tecnica archiviata (configurazione + PDF)");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio della consulenza");
    } finally { setSavingCt(false); }
  };


  const apply = () => {
    if (!result?.righe?.length) return;
    const tc = {
      config_id: selected.id,
      consulenza_url: selected.consulenza_url || null,
      public_token: selected.public_token || null,
      cliente: selected.summary?.cliente || null,
      tipo_copertura: tipo,
      disposizione_falde: falde,
      totali: result.totali,
      calcolato_il: new Date().toISOString(),
    };
    onApply(result.righe, tc, result.modulo_riga);
    onOpenChange(false);
    toast.success(`${result.righe.length} righe di fissaggio${result.modulo_riga ? " + moduli" : ""} inserite nel preventivo`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="fissaggi-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Ruler className="h-5 w-5 text-primary" /> Dimensiona strutture di fissaggio</DialogTitle></DialogHeader>

        <div className="space-y-5">
          {/* 1. pannello */}
          <div className="rounded-lg border border-input p-3">
            <span className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-widest text-primary">1 · Pannello fotovoltaico</span>
            <Select value={moduleId} onValueChange={(v) => { setModuleId(v); setResult(null); }}>
              <SelectTrigger data-testid="fissaggi-modulo"><SelectValue placeholder="Scegli il modulo…" /></SelectTrigger>
              <SelectContent>{modules.map((m) => {
                const d = dimLabel(m);
                return (
                  <SelectItem key={m.id} value={m.id}>
                    <span>{m.codice}</span>
                    {d && <span className="ml-2 text-xs text-muted-foreground">· {d}</span>}
                  </SelectItem>
                );
              })}</SelectContent>
            </Select>
            {(() => {
              const m = modules.find((x) => x.id === moduleId);
              if (!m) {
                return <p className="mt-1 text-[11px] text-muted-foreground">Le dimensioni arrivano dal Listino e vengono inviate al configuratore. Numero pannelli e fissaggi verranno inseriti nel preventivo.</p>;
              }
              const { w, l, wp } = panelDims(m);
              const missing = !w || !l;
              return (
                <div className="mt-2 space-y-1.5" data-testid="fissaggi-modulo-dims">
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="secondary" data-testid="fissaggi-dim-larghezza">Larghezza {w ? `${w} mm` : "—"}</Badge>
                    <Badge variant="secondary" data-testid="fissaggi-dim-lunghezza">Lunghezza {l ? `${l} mm` : "—"}</Badge>
                    <Badge variant="secondary" data-testid="fissaggi-dim-potenza">Potenza {wp ? `${wp} Wp` : "—"}</Badge>
                  </div>
                  {missing ? (
                    <div className="flex items-center gap-1.5 rounded-md border border-amber-400/40 bg-amber-400/10 p-1.5 text-[11px] text-amber-600 dark:text-amber-300" data-testid="fissaggi-dim-missing">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Dimensione mancante a Listino — arricchisci la scheda dal gestionale, altrimenti il configuratore non la riceve.
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Misure lette dal Listino: vengono inviate al configuratore. Numero pannelli e fissaggi verranno inseriti nel preventivo.</p>
                  )}
                </div>
              );
            })()}
          </div>

          {/* 2. consulenza tetto */}
          <div className="rounded-lg border border-input p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-primary">2 · Layout tetto (configuratore)</span>
              <div className="flex flex-wrap gap-2">
                {!adminAll && !link && <Button type="button" size="sm" variant="outline" disabled={!moduleId} onClick={openConfiguratore} data-testid="fissaggi-open-cfg"><ExternalLink className="mr-1.5 h-4 w-4" /> Apri configuratore tetto</Button>}
                {!link && <Button type="button" size="sm" variant="ghost" onClick={loadConfigs} data-testid="fissaggi-reload"><RefreshCw className={`mr-1.5 h-4 w-4 ${loadingCfg ? "animate-spin" : ""}`} /> Aggiorna consulenze</Button>}
              </div>
            </div>
            {adminAll && (
              <Input value={cfgQ} onChange={(e) => setCfgQ(e.target.value)} placeholder="Cerca per azienda, agente o comune…" className="mb-2 h-8 text-xs" data-testid="admin-cfg-search" />
            )}
            {loadingCfg ? (
              <div className="py-4 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
            ) : configs.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">Nessuna consulenza {inclinatoOnly ? "tetto inclinato" : pianoOnly ? "tetto piano" : "tetto"} adatta{adminAll ? "" : " per questo cliente. Aprine una nel configuratore, poi premi «Aggiorna consulenze»"}.</p>
            ) : (
              <div className="space-y-1.5" data-testid="fissaggi-config-list">
                {configs.filter((c) => !adminAll || cfgMatch(c, cfgQ)).map((c) => {
                  const sel = selected?.id === c.id;
                  const s = c.summary || {};
                  const exactLabel = configPanelExact(configPanel(s));
                  const incompatible = isIncompatible(c);
                  const meta = [new Date(c.created_at).toLocaleDateString("it-IT")];
                  if (s.comune) meta.push(s.comune);
                  meta.push(`${s.n_pannelli ?? "—"} pann.`);
                  meta.push(`${s.potenza_kwp ?? "—"} kWp`);
                  if (s.superficie_mq) meta.push(`${s.superficie_mq} m²`);
                  return (
                    <button key={c.id} type="button" disabled={incompatible}
                      onClick={() => { if (incompatible) return; setSelected(c); setResult(null); }} data-testid={`fissaggi-config-${c.id}`}
                      className={`flex w-full items-center justify-between gap-2 rounded-md border p-2 text-left text-sm transition-colors ${incompatible ? "cursor-not-allowed border-destructive/40 bg-destructive/5 opacity-70" : sel ? "border-primary bg-primary/10" : "border-input hover:bg-accent"}`}>
                      <span className="min-w-0">
                        {adminAll && <AdminCfgLine c={c} />}
                        <span className="font-semibold">{s.cliente || "—"}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{meta.join(" · ")}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5">
                          {incompatible
                            ? <Badge variant="destructive" className="gap-1 text-[10px]" data-testid={`fissaggi-config-wp-${c.id}`}><AlertTriangle className="h-3 w-3" /> Pannello diverso{exactLabel ? ` · ${exactLabel}` : ""}</Badge>
                            : exactLabel && <Badge variant="secondary" className="text-[10px]" data-testid={`fissaggi-config-wp-${c.id}`}>{exactLabel}</Badge>}
                        </span>
                        <span className="block text-[11px] leading-snug text-muted-foreground">{faldeSummary(s.disposizione_falde)}</span>
                      </span>
                      {sel && !incompatible && <Check className="h-4 w-4 shrink-0 text-primary" />}
                    </button>
                  );
                })}
              </div>
            )}
            {configs.length > 0 && (
              <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="fissaggi-falde-legenda">
                Legenda: <b>file</b> = numero di righe di pannelli sulla falda; <b>pann./fila</b> = quanti pannelli ci sono in ciascuna riga (usati per calcolare i metri di binario e i tagli). Se hai scelto un pannello sopra, le consulenze realizzate con un pannello di misura diversa sono segnate <b>«Pannello diverso»</b> e non selezionabili, per evitare associazioni sbagliate.
              </p>
            )}
            {selected && !hasFalde && (
              <div className="mt-2 flex items-center gap-1.5 rounded-md border border-amber-400/40 bg-amber-400/10 p-2 text-xs text-amber-600 dark:text-amber-300" data-testid="fissaggi-nofalde">
                <AlertTriangle className="h-4 w-4" /> Questa consulenza non ha ancora il layout falde (rows). Completa il layout nel configuratore e riprova.
              </div>
            )}
            {selected?.consulenza_url && (
              <a href={selected.consulenza_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="fissaggi-link"><ExternalLink className="h-3.5 w-3.5" /> Apri consulenza selezionata</a>
            )}
            {selected && hasFalde && <LayoutPreview falde={falde} latiFalde={latiFalde} />}
          </div>

          {/* 2. tipo copertura */}
          <div className="rounded-lg border border-input p-3">
            <span className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-widest text-primary">3 · Tipo di copertura</span>
            {roofType && (
              <p className="mb-2 text-[11px] text-muted-foreground" data-testid="fissaggi-rooftype-hint">
                Rilevato dal configuratore: <span className="font-semibold text-foreground">{roofType === "piano" ? "Tetto piano" : "Tetto inclinato"}</span>
                {roofType === "piano" ? " — preselezionato automaticamente" : " — scegli tra Coppo/Tegola o Lamiera"}
              </p>
            )}
            <div className="inline-flex flex-wrap gap-1 rounded-md border border-input p-0.5" data-testid="fissaggi-tipo">
              {TIPI.filter((t) => {
                if (inclinatoOnly) return t.key !== "piano";
                if (pianoOnly) return t.key === "piano";
                return true;
              }).map((t) => (
                <button key={t.key} type="button" onClick={() => { setTipo(t.key); setResult(null); }} data-testid={`fissaggi-tipo-${t.key}`}
                  className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${tipo === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{t.label}</button>
              ))}
            </div>

            {/* 3. articoli + dimensioni */}
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              {tipo === "coppo_tegola" && (
                <div><Label className="text-xs">Staffa / aggancio</Label>
                  <Select value={staffa} onValueChange={setStaffa}><SelectTrigger data-testid="fissaggi-staffa"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAFFE.map((s) => {
                      const info = staffeInfo[s.code] || {};
                      return (
                        <SelectItem key={s.code} value={s.code}>
                          <span className="flex items-center gap-2">
                            {info.immagine_url ? <img src={info.immagine_url} alt="" className="h-7 w-7 shrink-0 rounded bg-white object-contain" /> : null}
                            <span>{s.label}</span>
                          </span>
                        </SelectItem>
                      );
                    })}</SelectContent></Select>
                  {(() => {
                    const info = staffeInfo[staffa] || {};
                    if (!info.immagine_url && !info.descrizione) return null;
                    return (
                      <div className="mt-2 flex items-start gap-3 rounded-md border border-input bg-muted/30 p-2" data-testid="fissaggi-staffa-preview">
                        {info.immagine_url ? <img src={info.immagine_url} alt={staffa} className="h-16 w-16 shrink-0 rounded bg-white object-contain" /> : null}
                        <p className="text-[11px] leading-snug text-muted-foreground">{info.descrizione || ""}</p>
                      </div>
                    );
                  })()}
                </div>
              )}
              {tipo === "piano" && (
                <>
                  <div><Label className="text-xs">Inclinazione presagomata</Label>
                    <div className="mt-1 flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm" data-testid="fissaggi-gradi">
                      <b>{gradi}°</b><span className="ml-2 text-[11px] text-muted-foreground">— dalla consulenza</span>
                    </div>
                    {selDims?.w > 0
                      ? <p className="mt-1 text-[11px] text-muted-foreground" data-testid="fissaggi-prsg-info">Presagomata scelta automaticamente per interasse ≈ <b>{selDims.w} mm</b> (pannello, tolleranza 10 mm).</p>
                      : <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-300">Seleziona il pannello per abbinare la presagomata compatibile.</p>}
                  </div>
                  <div><Label className="text-xs">Zavorra</Label>
                    <Select value={zavorra} onValueChange={setZavorra}><SelectTrigger data-testid="fissaggi-zavorra"><SelectValue /></SelectTrigger>
                      <SelectContent>{ZAVORRE.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent></Select></div>
                </>
              )}
              {tipo !== "piano" && <p className="mt-1 text-[11px] text-muted-foreground">Le dimensioni del modulo scelto vengono lette dal Listino.</p>}
            </div>
          </div>

          {/* lato di fissaggio (tetto inclinato) — per falda */}
          {tipo !== "piano" && (
            <div className="rounded-lg border border-input p-3 space-y-3" data-testid="fissaggi-lato-block">
              <div>
                <Label className="text-xs">Lato di fissaggio del pannello (per falda)</Label>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Insieme all'orientamento determina <b>direzione e lunghezza</b> delle barre. Le barre sono continue e lunghe quanto la somma dei pannelli affiancati nel loro verso. Puoi impostare un lato diverso per ogni falda.
                </p>
              </div>
              {(falde || []).map((f, i) => {
                const orizz_i = isHoriz(f.orientamento || f.orientation || "verticale");
                const lato_i = latiFalde[i] || "corto";
                const barreOrizz_i = (lato_i === "corto" && orizz_i) || (lato_i === "lungo" && !orizz_i);
                return (
                  <div key={i} className="rounded-md border border-input bg-muted/20 p-2" data-testid={`fissaggi-falda-lato-${i}`}>
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-xs font-semibold">Falda {i + 1} · pannello {orizz_i ? "orizzontale" : "verticale"}</span>
                      <Badge variant="outline" className="text-[10px]">{(f.rows || []).join("·")}</Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1 max-w-[210px]">
                        <Select value={lato_i} onValueChange={(v) => { setLatiFalde((prev) => { const n = [...prev]; n[i] = v; return n; }); setResult(null); }}>
                          <SelectTrigger data-testid={`fissaggi-lato-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="corto">Fissaggio su lato corto</SelectItem>
                            <SelectItem value="lungo">Fissaggio su lato lungo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 rounded-md border border-input bg-background p-1.5">
                        <svg width="48" height="44" viewBox="0 0 120 110" className="shrink-0">
                          {(() => {
                            const pw = orizz_i ? 92 : 56, ph = orizz_i ? 56 : 92;
                            const x = (120 - pw) / 2, y = (110 - ph) / 2;
                            const bars = [0.28, 0.72].map((t, bi) => (barreOrizz_i
                              ? <line key={bi} x1={x - 6} y1={y + ph * t} x2={x + pw + 6} y2={y + ph * t} stroke="#2563eb" strokeWidth="3" strokeDasharray="5 3" />
                              : <line key={bi} x1={x + pw * t} y1={y - 6} x2={x + pw * t} y2={y + ph + 6} stroke="#2563eb" strokeWidth="3" strokeDasharray="5 3" />));
                            return (<>
                              <rect x={x} y={y} width={pw} height={ph} rx="3" fill="#fbbf24" fillOpacity="0.25" stroke="#a16207" strokeWidth="1.5" />
                              {bars}
                            </>);
                          })()}
                        </svg>
                        <div className="text-[10px] leading-tight">
                          <div className="font-semibold text-foreground" data-testid={`fissaggi-schema-titolo-${i}`}>Barre {barreOrizz_i ? "orizzontali · Modo A" : "verticali · Modo B"}</div>
                          <div className="text-muted-foreground">Lungo {barreOrizz_i ? "le file" : "le colonne"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-input pt-3">
                <Label className="text-xs">Numero binari (barre) per fila</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" className="h-8 w-8 p-0 text-base" onClick={() => { setBinariOverride(Math.max(1, effBinari - 1)); setResult(null); }} data-testid="fissaggi-binari-dec">−</Button>
                  <span className="min-w-8 text-center text-sm font-bold" data-testid="fissaggi-binari-val">{effBinari}</span>
                  <Button type="button" size="sm" variant="outline" className="h-8 w-8 p-0 text-base" onClick={() => { setBinariOverride(effBinari + 1); setResult(null); }} data-testid="fissaggi-binari-inc">+</Button>
                  {binariOverride != null && binariOverride !== autoBinari && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setBinariOverride(null); setResult(null); }} data-testid="fissaggi-binari-reset">Auto ({autoBinari})</Button>
                  )}
                </div>
                {binariOverride != null && binariOverride !== autoBinari ? (
                  <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-300" data-testid="fissaggi-binari-alert">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Valore <b>fuori default</b>: il calcolo standard prevede <b>{autoBinari}</b> {autoBinari === 1 ? "binario" : "binari"} per fila. Stai forzando <b>{binariOverride}</b>.
                  </p>
                ) : (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">Default automatico: <b>{autoBinari}</b> per fila (vale per tutte le falde). Usa +/− per forzare un valore diverso.</p>
                )}
              </div>
            </div>
          )}

          {/* interasse staffe (ultimo input prima del calcolo) */}
          {tipo === "coppo_tegola" && (
            <div className="rounded-lg border border-amber-400/40 bg-amber-400/5 p-3" data-testid="fissaggi-interasse-block">
              <Label className="text-xs">Interasse staffe (m)</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="number" step="0.05" min="0.1" value={passoStaffa}
                  onChange={(e) => { setPassoStaffa(e.target.value); setResult(null); }}
                  className="w-28" data-testid="fissaggi-interasse" />
                <Button type="button" size="sm" variant="ghost" onClick={() => { setPassoStaffa("1"); setResult(null); }} data-testid="fissaggi-interasse-reset">Ripristina 1</Button>
              </div>
              <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Sconsigliamo di modificare questo valore: il default è <b>1 m</b> (una staffa ogni 1 m per binario). Cambiarlo altera il numero di staffe calcolate.
              </p>
            </div>
          )}

          {/* calcola */}
          <div className="flex justify-end">
            <Button type="button" onClick={compute} disabled={!moduleId || !selected || !hasFalde || computing} data-testid="fissaggi-compute">
              {computing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Calculator className="mr-1.5 h-4 w-4" />} Calcola fissaggi
            </Button>
          </div>

          {/* risultato */}
          {result && (
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="fissaggi-result">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="secondary">{result.totali?.pannelli} pannelli</Badge>
                <Badge variant="secondary">{result.totali?.n_file} file</Badge>
                {result.totali?.metri_totali > 0 && <Badge variant="secondary">{result.totali.metri_totali} m binari</Badge>}
                {result.totali?.n_binari > 0 && <Badge variant="secondary">{result.totali.n_binari} binari/fila</Badge>}
                {(result.falde_usate || []).map((f, i) => (
                  <Badge key={i} variant="outline" data-testid={`fissaggi-falda-rows-${i}`}>Falda {i + 1}: {(f.rows || []).join("·")}</Badge>
                ))}
              </div>
              {result.warnings?.length > 0 && (
                <div className="mb-2 space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-300" data-testid="fissaggi-warnings">
                  {result.warnings.map((w, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}</div>)}
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground"><tr><th className="p-1 text-left">Articolo</th><th className="p-1 text-left">Codice</th><th className="p-1 text-right">Q.tà</th><th className="p-1 text-right">Prezzo</th><th className="p-1 text-right">Totale</th></tr></thead>
                <tbody>
                  {result.righe.map((r, i) => (
                    <tr key={i} className="border-t border-input" data-testid={`fissaggi-res-row-${i}`}>
                      <td className="p-1">{r.descrizione}</td>
                      <td className="p-1 font-mono text-xs">{r.codice}</td>
                      <td className="p-1 text-right font-semibold">{r.quantita}</td>
                      <td className="p-1 text-right text-muted-foreground">{eurN(r.prezzo_unitario)}</td>
                      <td className="p-1 text-right font-semibold">{eurN((r.quantita || 0) * (r.prezzo_unitario || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-[11px] text-muted-foreground">{pdfMode ? "Verrà generato un allegato tecnico PDF (layout + distinta articoli di fissaggio) collegato alla consulenza." : <>Confermando, le righe della categoria <b>Fissaggio</b> nel preventivo verranno sostituite con queste.</>}</p>
              {result.totali?.piano_taglio && (
                <div className="mt-3 border-t border-input pt-2" data-testid="fissaggi-piano-taglio">
                  <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="outline">{result.totali.barre_intere} barre intere</Badge>
                    <Badge variant="outline">{result.totali.barre_taglio} barre da tagliare</Badge>
                    <Badge variant="outline">scarto {result.totali.scarto_m} m{result.totali.scarto_pct != null ? ` · ${result.totali.scarto_pct}%` : ""}</Badge>
                    {result.totali.scarto_eur != null && <Badge variant="outline">scarto {eurN(result.totali.scarto_eur)}</Badge>}
                    <Badge variant="outline">{result.totali.giunti} giunti</Badge>
                  </div>
                  {(result.totali.dettaglio_file || []).length > 0 && (
                    <div className="mb-2">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Composizione per fila (per binario)</div>
                      {result.totali.dettaglio_file.map((d, i) => (
                        <div key={i} className="text-xs text-muted-foreground" data-testid={`fissaggi-fila-${i}`}>Fila {i + 1} · {d.P} pann. · {d.metri_fila} m → {(d.segmenti || []).join(" + ")} m · {d.giunti} giunti ×{d.binari}</div>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Schema di taglio barre (avanzi riutilizzati)</div>
                  {result.totali.piano_taglio.map((b, i) => (
                    <div key={i} className="text-xs" data-testid={`fissaggi-barra-${i}`}>Barra #{i + 1}: <span className="font-semibold">{(b.pezzi || []).join(" + ")} m</span> <span className="text-muted-foreground">→ scarto {b.scarto} m</span></div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex justify-end gap-2">
                {pdfMode
                  ? <>
                      <Button type="button" variant="outline" onClick={downloadPdf} disabled={pdfLoading} data-testid="fissaggi-pdf">{pdfLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Scarica PDF allegato tecnico</Button>
                      <Button type="button" onClick={saveConsulenza} disabled={savingCt} data-testid="fissaggi-save-ct">{savingCt ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Archive className="mr-1.5 h-4 w-4" />} Salva consulenza</Button>
                    </>
                  : <Button type="button" onClick={apply} data-testid="fissaggi-apply"><Check className="mr-1.5 h-4 w-4" /> Inserisci nel preventivo</Button>}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
